import { useEffect, useRef, useState } from "react";
import { Search, Trash2, UserRound } from "lucide-react";
import api from "@/lib/api";
import ReportQrScanner from "./ReportQrScanner";
import { addRelatedPerson, defaultRelationship, personKey, removeRelatedPerson, updateRelatedPersonRelationship } from "@/pages/Dashboard/reportingUtils";

export const RELATIONSHIPS = [
  ["PRIMARY_SUBJECT", "Primary subject"],
  ["INVOLVED_PERSON", "Involved person"],
  ["AFFECTED_VICTIM", "Affected / victim"],
  ["WITNESS", "Witness"],
  ["CONCERNED_STAFF", "Concerned staff"],
  ["OTHER", "Other"],
];

const initials = (name) => (name || "?").split(" ").slice(0, 2).map((part) => part[0]).join("").toUpperCase();
const context = (person) => [person.program, person.class_name, person.arm, person.position, person.unit].filter(Boolean).join(" — ");

export default function RelatedPeopleSelector({ value, onChange }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [message, setMessage] = useState("");
  const timer = useRef();

  useEffect(() => {
    clearTimeout(timer.current);
    if (query.trim().length < 2) { setResults([]); return undefined; }
    timer.current = setTimeout(() => api.get("/reports/people/search", { params: { q: query } })
      .then((response) => setResults(response.data))
      .catch(() => setMessage("People search is unavailable. Try again.")), 250);
    return () => clearTimeout(timer.current);
  }, [query]);

  const add = (person, source = "search") => {
    if (value.some((existing) => personKey(existing) === personKey(person))) {
      setMessage(`${person.name || "This person"} is already selected.`);
      return false;
    }
    onChange(addRelatedPerson(value, { ...person, relationship: person.relationship || defaultRelationship(person) }));
    setMessage("");
    if (source === "search") { setQuery(""); setResults([]); }
    return true;
  };
  const updateRelationship = (key, relationship) => onChange(updateRelatedPersonRelationship(value, key, relationship));
  const remove = (key) => onChange(removeRelatedPerson(value, key));

  return <div className="min-w-0 space-y-5">
    <div className="relative">
      <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
      <input className="w-full rounded-lg border py-2.5 pl-10 pr-3" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search name, ID, programme, class, arm, position or unit" data-testid="related-search" />
    </div>
    {results.length > 0 && <div className="max-h-64 divide-y overflow-auto rounded-lg border bg-white shadow-sm" role="listbox" aria-label="People search results">
      {results.map((person) => <button type="button" role="option" key={personKey(person)} onClick={() => add(person)} className="flex min-h-11 w-full min-w-0 items-center gap-3 p-3 text-left hover:bg-lime-50 focus:bg-lime-50 focus:outline-none" data-testid={`select-${person.person_type}`}>
        <div className="grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-full bg-[#1B2C5A] text-xs text-white">{person.photo ? <img src={person.photo} alt="" className="h-full w-full object-cover" /> : initials(person.name)}</div>
        <div className="min-w-0"><div className="truncate font-medium text-[#1B2C5A]">{person.name}</div><div className="truncate text-xs text-slate-500">{person.person_type === "student" ? "Student" : "Staff"} · {person.identity}{context(person) && ` · ${context(person)}`}</div></div>
      </button>)}
    </div>}
    <ReportQrScanner selectedKeys={value.map(personKey)} onAdd={(person) => add(person, "qr")} />
    {message && <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" role="alert">{message}</div>}
    <div>
      <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-[#1B2C5A]">Selected People ({value.length})</h3>
      <div className="grid gap-3 sm:grid-cols-2" data-testid="selected-people">
        {value.map((person) => <article key={personKey(person)} className="flex min-w-0 gap-3 rounded-xl border bg-white p-3">
          <div className="grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-full bg-[#1B2C5A] text-xs text-white">{person.photo ? <img src={person.photo} alt="" className="h-full w-full object-cover" /> : <><UserRound className="h-4 w-4" /><span className="sr-only">{initials(person.name)}</span></>}</div>
          <div className="min-w-0 flex-1"><div className="break-words font-medium text-[#1B2C5A]">{person.name || person.identity}</div><div className="break-all text-xs text-slate-500"><span className="rounded bg-slate-100 px-1.5 py-0.5">{person.person_type === "student" ? "Student" : "Staff"}</span> · {person.identity}</div>{context(person) && <div className="mt-1 break-words text-xs text-slate-500">{context(person)}</div>}<label className="mt-2 block text-xs font-medium">Relationship<select className="mt-1 w-full min-w-0 rounded border p-2 text-xs" value={person.relationship} onChange={(event) => updateRelationship(personKey(person), event.target.value)} data-testid="selected-relationship">{RELATIONSHIPS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}</select></label></div>
          <button type="button" aria-label={`Remove ${person.name || "person"}`} onClick={() => remove(personKey(person))} className="self-start rounded p-1.5 text-red-700 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
        </article>)}
        {!value.length && <div className="sm:col-span-2 rounded-lg border border-dashed p-5 text-center text-sm text-slate-500">No people selected yet.</div>}
      </div>
    </div>
  </div>;
}
